<script setup lang="ts">
/**
 * Mobile row for the Groups pane — denser than a simple list cell, so it
 * mirrors the sketch's `.g-row` directly instead of reusing the generic
 * mobile-cell convention: description+start/room on the left, state+
 * performers (one per line, colored by gender) stacked top-right, schedule
 * chips and service-type pills below. Still a tappable row (opens
 * `GroupMobileScreen` for edit/delete), like every other mobile list row
 * in this app.
 */
import { useI18n } from 'vue-i18n'

defineProps<{
  displayName: string
  startedLabel: string
  room: string | null
  active: boolean
  performers: { id: string; name: string; gender: 'm' | 'f' | string }[]
  scheduleLines: string[]
  serviceTypeLabels: string[]
}>()

const { t } = useI18n()
</script>

<template>
  <td class="group-mobile-cell font-tufte text-tufte-text">
    <div class="group-mobile-cell__top">
      <div class="group-mobile-cell__main">
        <div class="group-mobile-cell__name">{{ displayName }}</div>
        <div class="group-mobile-cell__started">{{ startedLabel }}</div>
        <div class="group-mobile-cell__room">
          <template v-if="room">{{ t('cards.group.roomLabel', { room }) }}</template>
          <template v-else>{{ t('cards.group.roomNotSet') }}</template>
        </div>
      </div>
      <div class="group-mobile-cell__side">
        <span :class="active ? 'group-mobile-cell__state--on' : 'group-mobile-cell__state--off'">
          {{ active ? t('cards.group.stateActive') : t('cards.group.stateInactive') }}
        </span>
        <div v-if="performers.length" class="group-mobile-cell__perfs">
          <span
            v-for="p in performers"
            :key="p.id"
            :class="p.gender === 'm' ? 'group-mobile-cell__perf--m' : 'group-mobile-cell__perf--f'"
          >
            {{ p.name }}
          </span>
        </div>
        <span v-else class="group-mobile-cell__perf-none">{{
          t('cards.group.performersNotSet')
        }}</span>
      </div>
    </div>

    <div v-if="scheduleLines.length" class="group-mobile-cell__sched">
      <span v-for="(line, i) in scheduleLines" :key="i" class="group-mobile-cell__sl">{{
        line
      }}</span>
    </div>
    <span v-else class="group-mobile-cell__sched-none">{{ t('cards.group.noSchedule') }}</span>

    <div v-if="serviceTypeLabels.length" class="group-mobile-cell__svcs">
      <span v-for="label in serviceTypeLabels" :key="label" class="group-mobile-cell__svc">{{
        label
      }}</span>
    </div>
  </td>
</template>

<style scoped>
.group-mobile-cell {
  /* Padding comes from the parent tab's `.groups-table--mobile :deep(td)`
   * override, matching every other mobile cell's convention (e.g.
   * `ClientMobileLeftCell`) — not set here so the two never drift. */
  display: block;
}

.group-mobile-cell__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.7rem;
}

.group-mobile-cell__name {
  font-size: var(--text-tufte-lg);
  line-height: 1.25;
}

.group-mobile-cell__started,
.group-mobile-cell__room {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  margin-top: 0.1rem;
}

.group-mobile-cell__side {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  max-width: 10.5rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
}

.group-mobile-cell__state--on {
  color: var(--color-tufte-success);
}

.group-mobile-cell__state--off {
  color: var(--color-tufte-muted);
}

.group-mobile-cell__perfs {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.08rem;
}

.group-mobile-cell__perf--m {
  color: var(--color-gender-men);
}

.group-mobile-cell__perf--f {
  color: var(--color-gender-women);
}

.group-mobile-cell__perf-none {
  color: var(--color-tufte-muted);
  font-style: italic;
  text-align: right;
}

.group-mobile-cell__sched {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.35rem;
  margin-top: 0.45rem;
}

.group-mobile-cell__sl {
  display: inline-flex;
  border: 1px solid var(--color-tufte-border);
  padding: 0.04rem 0.4rem;
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-xs);
  white-space: nowrap;
}

.group-mobile-cell__sched-none {
  display: block;
  margin-top: 0.4rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  font-style: italic;
}

.group-mobile-cell__svcs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.3rem;
  margin-top: 0.35rem;
}

.group-mobile-cell__svc {
  background: var(--color-surface-success);
  color: var(--color-tufte-success);
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  padding: 0.04rem 0.38rem;
  white-space: nowrap;
}
</style>
